/**
 * S3 path types for different asset categories
 *
 * Mapping for repodas:
 * - video_report_assets: Base path for video report files (videos/reports)
 *   Pass "{id}/thumbnail.jpg" or "{id}/video.mp4" as path
 * - user_avatars: user profile pictures
 *
 * Legacy support for atoindex:
 * - video_input: input videos
 * - video_output: output videos
 */
const pathToS3Type = {
  // repodas structure: videos/reports/{id}/thumbnail.jpg
  video_report_assets: (import.meta.env.VITE_APP_S3_INPUT_PATH || 'videos') + '/reports',
  user_avatars: 'avatars',
  // Legacy support for atoindex compatibility
  video_input: import.meta.env.VITE_APP_S3_INPUT_PATH || 'videos/input',
  video_output: import.meta.env.VITE_APP_S3_OUTPUT_PATH || 'videos/output',
} as const;

export type S3PathType = keyof typeof pathToS3Type;

/**
 * Get presigned S3 URL for an asset via /assets/ endpoint
 *
 * The backend /assets/ endpoint will:
 * 1. Generate presigned URL for the S3 path
 * 2. Redirect to the presigned URL
 * 3. Browser fetches the asset from S3
 *
 * @param path - Asset identifier (e.g., report ID, filename)
 * @param type - Asset category (determines S3 prefix)
 * @param controller - Optional AbortController for cancellation
 * @returns Presigned S3 URL (after redirect) or null on error
 */
export const getAssetsS3Path = async (
  path: string,
  type: S3PathType,
  controller?: AbortController
): Promise<string | null> => {
  const endpoint = import.meta.env.VITE_APP_BACKEND_END_POINT;
  const s3Prefix = pathToS3Type[type];

  if (!s3Prefix) {
    console.error(`Invalid S3 path type: ${type}`);
    return null;
  }

  try {
    const response = await fetch(`${endpoint}/assets/${s3Prefix}/${path}`, {
      signal: controller?.signal,
    });
    const data = await response.json();
    return data.url as string;
  } catch (error) {
    console.error('Failed to fetch asset URL:', error);
    return null;
  }
};

// Helper for video assets (compatible with annovideo's getAssetsImage)
export const getAssetsImage = async (
  path: string,
  type: 'input' | 'output' = 'output',
  controller?: AbortController
): Promise<string | null> => {
  const s3Type = type === 'output' ? 'video_output' : 'video_input';
  return getAssetsS3Path(path, s3Type, controller);
};
